import { useEffect, useState } from "react";
import { handleError } from "src/Common/function";
import Loader from "src/Components/Common/Loader";
import UserNotificationService from "src/Services/API/usernotification.service";
import SettingToggle from "./SettingToggle";

// Always-on rows: the backend delivers these regardless of preferences, so they
// render locked rather than being left out of the list.
const ALWAYS_ON = [
  {
    key: "WO_ASSIGNED",
    title: "New WO Assigned",
    description: "When a new work order is assigned to you",
  },
  {
    key: "TICKET_MESSAGE",
    title: "New Message Received",
    description: "Messages in My Tickets",
  },
  {
    key: "WO_DUE_TODAY",
    title: "WO Due Today",
    description: "Morning reminder for scheduled work orders due today",
  },
];

const OPTIONAL = [
  {
    key: "TECH_NOTE_ADDED",
    title: "New Tech Notes",
    description: "When a new Tech Note is added to one of your assigned work orders",
  },
];

const SettingRow = ({ title, description, isLast, control }) => (
  <div className={`flex items-center justify-between gap-6 p-4 ${isLast ? "" : "border-b-[1px] border-[#EEF0F9]"}`}>
    <div className="min-w-0">
      <div className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]">{title}</div>
      <div className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
        {description}
      </div>
    </div>
    {control}
  </div>
);

// Section heading with the hairline that runs out to the column edge.
const SectionLabel = ({ children }) => (
  <div className="w-full px-1 flex items-center gap-2">
    <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] uppercase text-[#7C829A]">
      {children}
    </span>
    <div className="flex-1 h-px bg-[#E5E8F3]" />
  </div>
);

/**
 * Per-user in-app notification preferences, reached from the gear in the bell
 * drawer. Every switch here is scoped to the signed-in user server-side; the
 * company-wide rules are a different page (Settings > Notification Center).
 *
 * The column is capped and centred rather than filling the viewport: rows that
 * run the width of a 1920 screen strand the toggle half a screen from the text
 * it belongs to.
 */
const NotificationSettings = () => {
  const [preferences, setPreferences] = useState(() =>
    OPTIONAL.reduce((acc, item) => ({ ...acc, [item.key]: true }), {})
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    UserNotificationService.getPreferences()
      .then((res) => {
        if (!cancelled && res?.data) setPreferences((prev) => ({ ...prev, ...res.data }));
      })
      .catch((error) => {
        if (!cancelled) handleError(error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Optimistic like the technician app: a switch position is not worth blocking
  // the UI over, and a failed write is rolled back.
  const togglePreference = (key) => {
    const next = !preferences[key];
    setPreferences((prev) => ({ ...prev, [key]: next }));
    UserNotificationService.updatePreferences({ [key]: next })
      .then((res) => {
        if (res?.data) setPreferences((prev) => ({ ...prev, ...res.data }));
      })
      .catch((error) => {
        setPreferences((prev) => ({ ...prev, [key]: !next }));
        handleError(error);
      });
  };

  return (
    <div className="p-5">
      <div className="mx-auto w-full max-w-[720px] flex flex-col gap-[27px]">
        {/* No heading here — the app header names the page. */}
        <p className="font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#7C829A]">
          Manage which in-app notifications you want to receive. Some important notifications are always enabled.
        </p>

        <div className="flex flex-col gap-3">
          <SectionLabel>Always On</SectionLabel>
          <div className="rounded-xl border-[1px] border-[#E5E8F3] bg-white overflow-hidden">
            {ALWAYS_ON.map((item, index) => (
              <SettingRow
                key={item.key}
                title={item.title}
                description={item.description}
                isLast={index === ALWAYS_ON.length - 1}
                control={<SettingToggle value locked />}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <SectionLabel>Optional Notifications</SectionLabel>
          <div className="rounded-xl border-[1px] border-[#E5E8F3] bg-white overflow-hidden">
            {OPTIONAL.map((item, index) => (
              <SettingRow
                key={item.key}
                title={item.title}
                description={item.description}
                isLast={index === OPTIONAL.length - 1}
                // Until the saved positions land a switch would be showing a
                // guess, so the spinner holds its place rather than flipping
                // under the user.
                control={
                  isLoading ? (
                    <Loader variant="inline" className="w-[44px] shrink-0" />
                  ) : (
                    <SettingToggle value={!!preferences[item.key]} onToggle={() => togglePreference(item.key)} />
                  )
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
